import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { MobileShell } from "@/components/MobileShell";
import { BrandHeader } from "@/components/BrandHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PinDialog } from "@/components/PinDialog";
import { formatMoney, parseAmountToCents, cleanCashtag } from "@/lib/money";
import { getDeviceId } from "@/lib/device";
import { toast } from "sonner";
import { Loader2, AtSign, ShieldAlert, Wallet as WalletIcon } from "lucide-react";
import { Link } from "react-router-dom";
import { SendPaypalDialog } from "@/components/SendPaypalDialog";

const Send = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [params] = useSearchParams();
  const [recipient, setRecipient] = useState(params.get("to") ?? "");
  const [amount, setAmount] = useState("50.00");
  const [note, setNote] = useState("");
  const [balance, setBalance] = useState<number | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [hasPinSet, setHasPinSet] = useState<boolean | null>(null);
  const [pinOpen, setPinOpen] = useState(false);
  const [paypalOpen, setPaypalOpen] = useState(false);

  useEffect(() => {
    if (!user) return;
    supabase.from("wallets").select("balance_cents").eq("user_id", user.id).maybeSingle()
      .then(({ data }) => setBalance((data as any)?.balance_cents ?? 0));
    supabase.rpc("has_pin").then(({ data }) => setHasPinSet(Boolean(data)));
  }, [user]);

  const validate = (): { tag: string; cents: number } | null => {
    const cents = parseAmountToCents(amount);
    if (!cents) {
      toast.error("Enter a valid amount");
      return null;
    }
    const tag = cleanCashtag(recipient);
    if (tag.length < 3) {
      toast.error("Enter a recipient $cashtag");
      return null;
    }
    return { tag, cents };
  };

  const handleStart = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    if (!hasPinSet) {
      toast.error("Set a wallet PIN in Settings first");
      return;
    }
    openPin();
  };

  // Idempotency key persists across retries of the SAME confirmation flow.
  // Generated when the PIN dialog opens; reset after success or cancel.
  const [idemKey, setIdemKey] = useState<string | null>(null);

  const openPin = () => {
    setIdemKey(crypto.randomUUID());
    setPinOpen(true);
  };

  const handleConfirm = async (pin: string) => {
    const v = validate();
    if (!v) return;
    const key = idemKey ?? crypto.randomUUID();
    if (!idemKey) setIdemKey(key);
    setSubmitting(true);
    const { data, error } = await supabase.functions.invoke("send-money", {
      body: {
        recipient_cashtag: v.tag,
        amount_cents: v.cents,
        pin,
        note: note || null,
        device_id: getDeviceId(),
        idempotency_key: key,
      },
    });
    setSubmitting(false);
    // Edge function returns { error } in body on 4xx — supabase-js surfaces as data.error too
    const errMsg = (error as any)?.message || (data as any)?.error;
    if (errMsg) {
      toast.error(errMsg);
      return;
    }
    setPinOpen(false);
    setIdemKey(null);
    toast.success(`Sent ${formatMoney(v.cents)} to $${v.tag} 🌴`);
    navigate("/wallet", { replace: true });
  };

  const cents = parseAmountToCents(amount) ?? 0;
  const insufficient = balance !== null && cents > balance;

  return (
    <MobileShell>
      <BrandHeader onBack={() => navigate(-1)}>
        <h1 className="text-center mt-5 font-display text-2xl font-bold">Send Money</h1>
      </BrandHeader>

      <form onSubmit={handleStart} className="px-5 py-7 space-y-5 flex-1 animate-fade-in-up">
        {hasPinSet === false && (
          <Link
            to="/settings"
            className="flex items-center gap-3 rounded-xl border border-destructive/40 bg-destructive/5 px-4 py-3 text-sm"
          >
            <ShieldAlert className="h-5 w-5 text-destructive shrink-0" />
            <span className="flex-1">Set a 4-digit wallet PIN to send money.</span>
            <span className="text-destructive font-medium">Set up</span>
          </Link>
        )}
        <Link
          to="/send-nearby"
          className="flex items-center gap-3 rounded-xl border border-primary/30 bg-primary/5 hover:bg-primary/10 px-4 py-3 text-sm transition"
        >
          <span className="grid h-9 w-9 place-items-center rounded-full bg-primary/15 text-primary">
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m7 7 10 10-5 5V2l5 5L7 17"/></svg>
          </span>
          <span className="flex-1">
            <span className="block font-medium">Nearby Transfer (Bluetooth)</span>
            <span className="block text-xs text-muted-foreground">Send to someone next to you — works offline</span>
          </span>
        </Link>
        <button
          type="button"
          onClick={() => setPaypalOpen(true)}
          className="w-full flex items-center gap-3 rounded-xl border border-[#0070ba]/30 bg-[#0070ba]/5 hover:bg-[#0070ba]/10 px-4 py-3 text-sm transition text-left"
        >
          <span className="grid h-9 w-9 place-items-center rounded-full bg-[#0070ba]/15 text-[#0070ba]">
            <WalletIcon className="h-5 w-5" />
          </span>
          <span className="flex-1">
            <span className="block font-medium">Send via PayPal</span>
            <span className="block text-xs text-muted-foreground">Pay anyone with a PayPal email — works worldwide</span>
          </span>
        </button>
        <div>
          <Label htmlFor="to" className="text-sm font-medium">To</Label>
          <div className="relative mt-2">
            <AtSign className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
            <Input
              id="to"
              autoFocus
              placeholder="Enter cashtag (e.g. john)"
              value={recipient}
              onChange={(e) => setRecipient(e.target.value)}
              className="pl-12 h-13 rounded-xl bg-secondary/60 border-transparent focus-visible:bg-background focus-visible:border-primary"
              style={{ height: "3rem" }}
            />
          </div>
        </div>

        <div>
          <Label htmlFor="amount" className="text-sm font-medium">Amount</Label>
          <div className="mt-2 flex items-center bg-secondary/60 rounded-xl px-5 h-16">
            <span className="font-display text-3xl font-bold text-muted-foreground mr-1">$</span>
            <input
              id="amount"
              inputMode="decimal"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="bg-transparent flex-1 font-display text-3xl font-bold outline-none w-full"
            />
          </div>
          <div className="mt-2 flex justify-between text-xs text-muted-foreground">
            <span>Balance: {balance !== null ? formatMoney(balance) : "—"}</span>
            <span>Fee: $0.00 (Est.)</span>
          </div>
          {insufficient && <p className="mt-2 text-xs text-destructive">Not enough balance</p>}
        </div>

        <div>
          <Label htmlFor="note" className="text-sm font-medium">Note (optional)</Label>
          <Input
            id="note"
            placeholder="What's it for?"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            maxLength={140}
            className="mt-2 h-12 rounded-xl bg-secondary/60 border-transparent focus-visible:bg-background focus-visible:border-primary"
          />
        </div>

        <Button
          type="submit"
          variant="hero"
          size="xl"
          className="w-full mt-4"
          disabled={submitting || insufficient || !recipient || cents === 0 || hasPinSet === false}
        >
          {submitting ? <Loader2 className="h-5 w-5 animate-spin" /> : `Send ${formatMoney(cents)}`}
        </Button>
      </form>

      <PinDialog
        open={pinOpen}
        title={`Send ${formatMoney(cents)}`}
        description={`Confirm sending to $${cleanCashtag(recipient)}`}
        onCancel={() => { setPinOpen(false); setIdemKey(null); }}
        onSubmit={handleConfirm}
        pending={submitting}
      />

      <SendPaypalDialog open={paypalOpen} onOpenChange={setPaypalOpen} />

      <div className="safe-bottom" />
    </MobileShell>
  );
};

export default Send;
